import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import PublishIcon from "@material-ui/icons/Publish";
import GetAppIcon from "@material-ui/icons/GetApp";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RefreshIcon from "@material-ui/icons/Refresh";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import formatPhone from "../../helpers/formatPhone";
import planilhaExemplo from "../../assets/planilha.xlsx";
import { useCampaignStyles, downloadCsv } from "../Campaigns/campaignShared";

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "true", label: "Com WhatsApp" },
  { key: "false", label: "Sem WhatsApp" },
];

const ContactListItems = () => {
  const classes = useCampaignStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { contactListId } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [list, setList] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [valid, setValid] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const [modal, setModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(null);
  const [confirmRemoveInvalid, setConfirmRemoveInvalid] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const fileRef = useRef(null);

  const loadList = useCallback(() => {
    api
      .get(`/contact-lists/${contactListId}`)
      .then(({ data }) => setList(data))
      .catch((err) => {
        toastError(err);
        history.push("/contact-lists");
      });
  }, [contactListId, history]);

  const reload = useCallback(() => {
    setContacts([]);
    setPageNumber(1);
    setReloadKey((k) => k + 1);
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Veio de "Importar" na lista: já abre a escolha do arquivo
  useEffect(() => {
    if (new URLSearchParams(location.search).get("importar") === "1") {
      history.replace(location.pathname);
      setTimeout(() => fileRef.current && fileRef.current.click(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setContacts([]);
    setPageNumber(1);
  }, [searchParam, valid]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contact-list-items", {
          params: { searchParam, pageNumber, contactListId, valid: valid || undefined },
        });
        setContacts((prev) => {
          const next = pageNumber === 1 ? [] : [...prev];
          data.contacts.forEach((c) => {
            const i = next.findIndex((x) => x.id === c.id);
            if (i !== -1) next[i] = c;
            else next.push(c);
          });
          return next;
        });
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        toastError(err);
      }
      setLoading(false);
    }, 400);
    return () => clearTimeout(t);
  }, [searchParam, valid, pageNumber, contactListId, reloadKey]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });
    socket.on(`company-${companyId}-ContactListItem`, (data) => {
      if (data.action === "delete") {
        setContacts((prev) => prev.filter((c) => c.id !== +data.id));
        loadList();
      }
      if ((data.action === "update" || data.action === "create") && String(data.record?.contactListId) === String(contactListId)) {
        setContacts((prev) => {
          const i = prev.findIndex((c) => c.id === data.record.id);
          if (i === -1) return [data.record, ...prev];
          const next = [...prev];
          next[i] = data.record;
          return next;
        });
        loadList();
      }
    });
    socket.on(`company-${companyId}-ContactListItem-${contactListId}`, (data) => {
      if (data.action === "reload") {
        if (data.recheck) {
          setRechecking(false);
          toast.success(
            data.recheck.nowValid > 0
              ? `${data.recheck.nowValid} de ${data.recheck.checked} contatos agora têm WhatsApp`
              : "Verificação concluída: nenhum contato novo com WhatsApp"
          );
        }
        reload();
      }
    });
    return () => socket.disconnect();
  }, [contactListId, loadList, reload]);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const handleImport = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = null;
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/contact-lists/${contactListId}/upload`, formData);
      setImportSummary(data.summary || null);
      reload();
    } catch (err) {
      toastError(err);
    }
    setImporting(false);
  };

  const handleDelete = async () => {
    const c = deleting;
    setDeleting(null);
    try {
      await api.delete(`/contact-list-items/${c.id}`);
      toast.success("Contato removido da lista");
      setContacts((prev) => prev.filter((x) => x.id !== c.id));
      loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const removeInvalid = async () => {
    setConfirmRemoveInvalid(false);
    try {
      const { data } = await api.delete(`/contact-lists/${contactListId}/invalid-items`);
      toast.success(`${data.removed} ${data.removed === 1 ? "contato removido" : "contatos removidos"}`);
      reload();
    } catch (err) {
      toastError(err);
    }
  };

  const recheck = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post(`/contact-lists/${contactListId}/recheck`);
      if (!data.total) {
        setRechecking(false);
        toast.info("Não há contatos sem WhatsApp para verificar");
      } else {
        toast.info(`Verificando ${data.total} contatos… avisamos quando terminar`);
      }
    } catch (err) {
      setRechecking(false);
      toastError(err);
    }
  };

  const exportCsv = async () => {
    try {
      const all = [];
      let page = 1;
      // a rota é paginada (20 por página): busca tudo
      for (;;) {
        const { data } = await api.get("/contact-list-items", { params: { contactListId, pageNumber: page } });
        all.push(...data.contacts);
        if (!data.hasMore || page > 500) break;
        page += 1;
      }
      downloadCsv(
        `lista-${(list?.name || contactListId).toString().replace(/[^\w-]+/g, "_")}.csv`,
        ["nome", "numero", "email", "whatsapp"],
        all.map((c) => [c.name, c.number, c.email, c.isWhatsappValid ? "sim" : "não"])
      );
    } catch (err) {
      toastError(err);
    }
  };

  const total = Number(list?.contactsCount || 0);
  const validCount = Number(list?.validCount || 0);
  const invalidCount = Math.max(0, total - validCount);
  const campaignsCount = Number(list?.campaignsCount || 0);

  const filterCount = { "": total, true: validCount, false: invalidCount };

  const waPill = (c) =>
    c.isWhatsappValid ? (
      <Chip size="small" label="Tem WhatsApp" className={`${classes.pill} ${classes.pillGreen}`} />
    ) : (
      <Chip size="small" label="Sem WhatsApp" className={`${classes.pill} ${classes.pillRed}`} />
    );

  return (
    <MainContainer>
      <input ref={fileRef} type="file" hidden accept=".xls,.xlsx,.csv" onChange={handleImport} />
      <ContactListItemModal
        open={modal.open}
        contactId={modal.id}
        contactListId={contactListId}
        onClose={() => setModal({ open: false, id: null })}
        onSave={() => reload()}
      />

      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover {deleting?.name} da lista?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Voltar</Button>
          <Button onClick={handleDelete} variant="contained" disableElevation style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRemoveInvalid} onClose={() => setConfirmRemoveInvalid(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover os {invalidCount} contatos sem WhatsApp?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.muted}>
            Eles já ficam de fora das campanhas. Se a conexão estava desligada na importação, use antes “Verificar de
            novo”.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveInvalid(false)}>Voltar</Button>
          <Button onClick={removeInvalid} variant="contained" disableElevation style={{ backgroundColor: theme.palette.error.main, color: "#fff" }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); fileRef.current && fileRef.current.click(); }}>
          <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Importar planilha" />
        </MenuItem>
        <MenuItem component="a" href={planilhaExemplo} download="planilha-modelo.xlsx" onClick={() => setMenuAnchor(null)}>
          <ListItemIcon><GetAppIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Baixar planilha modelo" />
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); exportCsv(); }}>
          <ListItemIcon><GetAppIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Exportar contatos (CSV)" />
        </MenuItem>
      </Menu>

      <div className={classes.root}>
        <div>
          <Button className={classes.backLink} startIcon={<ArrowBackIcon fontSize="small" />} onClick={() => history.push("/contact-lists")}>
            Listas de contatos
          </Button>
        </div>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <Title>
              {list?.name || "…"}
              {list && (
                <span style={{ fontSize: 15, fontWeight: 500, color: theme.palette.text.secondary }}>
                  {" "}· {total.toLocaleString("pt-BR")} {total === 1 ? "contato" : "contatos"}
                </span>
              )}
            </Title>
            <Typography variant="body2" className={classes.muted}>
              {campaignsCount ? `Usada em ${campaignsCount} ${campaignsCount === 1 ? "campanha" : "campanhas"}` : "Ainda não usada em campanhas"}
            </Typography>
          </div>
          <div className={classes.headerButtons}>
            {isMobile ? (
              <IconButton aria-label="Mais opções" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            ) : (
              <>
                <Button variant="outlined" className={`${classes.headerButton} ${classes.secondaryButton}`} startIcon={<GetAppIcon />} onClick={exportCsv}>
                  Exportar
                </Button>
                <Button
                  variant="outlined"
                  className={`${classes.headerButton} ${classes.secondaryButton}`}
                  startIcon={<PublishIcon />}
                  disabled={importing}
                  onClick={() => fileRef.current && fileRef.current.click()}
                >
                  {importing ? "Importando…" : "Importar planilha"}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              className={classes.headerButton}
              startIcon={<AddIcon />}
              onClick={() => setModal({ open: true, id: null })}
            >
              Adicionar contato
            </Button>
          </div>
        </div>

        {importing && (
          <div className={classes.callout} style={{ margin: "0 0 14px" }}>
            Importando e verificando os números no WhatsApp… listas grandes podem levar alguns minutos.
          </div>
        )}
        {importSummary && (
          <div className={classes.callout} style={{ margin: "0 0 14px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            <span style={{ flex: 1, minWidth: 240 }}>
              <b>Importação concluída:</b> {importSummary.total} linhas lidas · {importSummary.created} novos
              {importSummary.existing > 0 && ` · ${importSummary.existing} já estavam na lista`}
              {importSummary.ignored > 0 && ` · ${importSummary.ignored} ignorados (sem número)`}
              {importSummary.created > 0 && ` · ${importSummary.valid} com WhatsApp`}
              {importSummary.invalid > 0 && `, ${importSummary.invalid} sem`}.
            </span>
            {importSummary.invalid > 0 && (
              <Button size="small" color="primary" style={{ textTransform: "none", fontWeight: 600 }} onClick={() => setValid("false")}>
                Ver sem WhatsApp
              </Button>
            )}
            <Button size="small" style={{ textTransform: "none" }} onClick={() => setImportSummary(null)}>
              Fechar
            </Button>
          </div>
        )}

        <Paper className={classes.mainPaper} variant="outlined">
          <div className={classes.toolbar} style={isMobile ? { padding: theme.spacing(1.5) } : undefined}>
            <TextField
              className={classes.searchField}
              variant="outlined"
              size="small"
              placeholder="Nome, número ou e-mail"
              type="search"
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ "aria-label": "Buscar contato" }}
            />
            <div className={`${classes.chipGroup} ${isMobile ? classes.chipGroupScroll : ""}`} role="group" aria-label="WhatsApp">
              {FILTERS.map((f) => {
                const active = valid === f.key;
                return (
                  <Chip
                    key={f.key || "all"}
                    clickable
                    className={classes.filterChip}
                    label={list ? `${f.label} ${filterCount[f.key]}` : f.label}
                    color={active ? "primary" : "default"}
                    variant={active ? "default" : "outlined"}
                    aria-pressed={active}
                    onClick={() => setValid(f.key)}
                  />
                );
              })}
            </div>
            {invalidCount > 0 && (
              <div style={{ display: "flex", gap: 8, marginLeft: isMobile ? 0 : "auto", flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  className={`${classes.rowButton} ${classes.rowButtonOutlined}`}
                  startIcon={<RefreshIcon fontSize="small" />}
                  disabled={rechecking}
                  onClick={recheck}
                >
                  {rechecking ? "Verificando…" : "Verificar de novo"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.rowButton}
                  style={{ color: theme.palette.error.main, borderColor: theme.palette.error.main }}
                  onClick={() => setConfirmRemoveInvalid(true)}
                >
                  Remover os {invalidCount} sem WhatsApp
                </Button>
              </div>
            )}
          </div>

          <div className={classes.tableWrapper} onScroll={handleScroll}>
            {isMobile ? (
              <div>
                {contacts.map((c) => (
                  <div key={c.id} className={classes.mobileRow}>
                    <div className={classes.mobileInfo} onClick={() => setModal({ open: true, id: c.id })}>
                      <div className={classes.mobileTitle}>{c.name}</div>
                      <div className={classes.subtle}>{formatPhone(c.number)}</div>
                    </div>
                    {waPill(c)}
                    <IconButton size="small" aria-label={`Remover ${c.name}`} className={classes.deleteAction} onClick={() => setDeleting(c)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>Nome</TableCell>
                    <TableCell className={classes.headCell}>Número</TableCell>
                    <TableCell className={classes.headCell}>E-mail</TableCell>
                    <TableCell className={classes.headCell}>WhatsApp</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((c) => (
                    <TableRow key={c.id} className={classes.row}>
                      <TableCell style={{ paddingLeft: 20, fontWeight: 600 }}>{c.name}</TableCell>
                      <TableCell style={{ whiteSpace: "nowrap" }}>{formatPhone(c.number)}</TableCell>
                      <TableCell className={c.email ? undefined : classes.muted}>{c.email || "—"}</TableCell>
                      <TableCell>{waPill(c)}</TableCell>
                      <TableCell align="right" style={{ paddingRight: 12 }}>
                        <div className={classes.actions}>
                          <IconButton size="small" aria-label={`Editar ${c.name}`} className={classes.iconAction} onClick={() => setModal({ open: true, id: c.id })}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" aria-label={`Remover ${c.name}`} className={classes.deleteAction} onClick={() => setDeleting(c)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={5} />}
                </TableBody>
              </Table>
            )}
            {!loading && contacts.length === 0 && (
              <div className={classes.emptyState}>
                <PeopleOutlineIcon className={classes.emptyIcon} />
                {searchParam || valid ? (
                  <Typography variant="body2">Nenhum contato encontrado com esse filtro</Typography>
                ) : (
                  <>
                    <Typography variant="body2">
                      Lista vazia. Importe uma planilha com as colunas <b>nome</b>, <b>numero</b> e <b>email</b>, ou adicione
                      contatos um a um.
                    </Typography>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button color="primary" onClick={() => fileRef.current && fileRef.current.click()}>
                        Importar planilha
                      </Button>
                      <Button component="a" href={planilhaExemplo} download="planilha-modelo.xlsx">
                        Baixar modelo
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {contacts.length > 0 && (
            <div className={classes.footer}>
              Mostrando {contacts.length} de {count}
              {hasMore ? " · role para carregar mais" : ""}
            </div>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default ContactListItems;
